// DineEnhancements — menu surfaces for the Dine tab:
//
//   1. Sub-category tabs — À la carte / Pizza / Special events. Active
//      tab filters which units show in the pricing list.
//   2. Menu attachments — PDFs + links rendered as download chips.
//
// The seating-area picker that used to live here was extracted into
// SeatingAreaPicker so non-dining service types (wine tastings,
// picnics) can render it inline with the booking setup row. See
// SeatingAreaPicker.tsx — ServicePanel composes both.
//
// Renders nothing until service_menu_tree returns, so the host
// ServicePanel can drop this in unconditionally without a layout jump.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../config";

export interface MenuUnit {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  base_price_cents: number;
  child_price_cents: number | null;
  currency: string;
  image_url: string | null;
  display_order: number;
}

export interface MenuAttachment {
  id: string;
  name: string;
  kind: "pdf" | "link" | "image";
  url: string;
  display_order: number;
}

export interface Subcategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  image_url: string | null;
  display_order: number;
  units: MenuUnit[];
  menus: MenuAttachment[];
}

interface MenuTree {
  subcategories: Subcategory[];
  uncategorised_units: MenuUnit[];
  service_menus: MenuAttachment[];
}

interface Props {
  serviceId: string;
  selectedSubcategoryId: string | null;
  onSubcategoryChange: (id: string | null) => void;
}

export function DineEnhancements({
  serviceId,
  selectedSubcategoryId,
  onSubcategoryChange,
}: Props) {
  const [menu, setMenu] = useState<MenuTree | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.rpc("service_menu_tree", {
        p_service_id: serviceId,
      });
      if (cancelled) return;
      setMenu(
        (data ?? {
          subcategories: [],
          uncategorised_units: [],
          service_menus: [],
        }) as MenuTree,
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const activeSubcategory = useMemo(() => {
    if (!menu) return null;
    if (!selectedSubcategoryId) return null;
    return menu.subcategories.find((s) => s.id === selectedSubcategoryId) ?? null;
  }, [menu, selectedSubcategoryId]);

  if (!menu) return null;
  const hasSubcategories = menu.subcategories.length > 0;
  // Only show the unit list once a subcategory is actively selected.
  // The previous fallback to `uncategorised_units` ended up listing
  // physical seats/tables (configured as dining units) under the
  // menu — that's noise on the booking flow.
  const unitsForActive = activeSubcategory ? activeSubcategory.units : [];

  return (
    <div className="vt-dine-enhancements">
      {hasSubcategories && (
        <div className="vt-dine-subcategories">
          <div className="vt-dine-section-label">Menu section</div>
          <div className="vt-dine-tab-row">
            {menu.subcategories.map((sc) => (
              <button
                key={sc.id}
                type="button"
                className={`vt-dine-tab ${selectedSubcategoryId === sc.id ? "vt-dine-tab-active" : ""}`}
                onClick={() =>
                  onSubcategoryChange(selectedSubcategoryId === sc.id ? null : sc.id)
                }
              >
                {sc.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {unitsForActive.length > 0 && (
        <ul className="vt-dine-unit-list">
          {unitsForActive.map((u) => (
            <li key={u.id} className="vt-dine-unit">
              <div className="vt-dine-unit-text">
                <div className="vt-dine-unit-name">{u.name}</div>
                {u.description && <div className="vt-dine-unit-desc">{u.description}</div>}
              </div>
              <div className="vt-dine-unit-price">
                {formatPrice(u.base_price_cents, u.currency)}
                {u.child_price_cents !== null && (
                  <span className="vt-dine-unit-child">
                    / kids {formatPrice(u.child_price_cents, u.currency)}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {(activeSubcategory?.menus.length ?? 0) > 0 && (
        <MenuAttachmentRow attachments={activeSubcategory!.menus} />
      )}

      {menu.service_menus.length > 0 && (
        <MenuAttachmentRow attachments={menu.service_menus} label="Menus" />
      )}
    </div>
  );
}

function MenuAttachmentRow({
  attachments,
  label,
}: {
  attachments: MenuAttachment[];
  label?: string;
}) {
  return (
    <div className="vt-dine-attachments">
      {label && <div className="vt-dine-section-label">{label}</div>}
      <div className="vt-dine-chip-row">
        {attachments.map((a) => (
          <a
            key={a.id}
            href={a.url}
            target="_blank"
            rel="noreferrer"
            className="vt-chip vt-chip-link"
          >
            {iconFor(a.kind)} {a.name}
          </a>
        ))}
      </div>
    </div>
  );
}

function iconFor(kind: MenuAttachment["kind"]): string {
  if (kind === "pdf") return "📄";
  if (kind === "image") return "🖼";
  return "↗";
}

function formatPrice(cents: number, currency: string): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: currency || "ZAR",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(0)}`;
  }
}
